import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Landmark } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ClientProfile() {
  const { user, refreshUser } = useAuth();
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    api.get("/client/bank-info").then((r) => setBank(r.data)).catch(() => {});
  }, []);

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ old_password: "", new_password: "", confirm: "" });
      refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/client/statement", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `xm-forex-statement-${user.email}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Failed to generate statement");
    } finally {
      setDownloading(false);
    }
  };

  if (!user) return null;
  const hasBank = bank && (bank.bank_name || bank.account_number || bank.ifsc || bank.notes);

  return (
    <div className="space-y-px" data-testid="profile-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border max-w-5xl">
        <div className="bg-card p-8 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="label-eyebrow mb-2">Account</p>
              <h3 className="text-lg font-semibold">Profile Details</h3>
            </div>
            <Button onClick={downloadStatement} disabled={downloading} variant="outline" className="gap-2 h-9 text-xs" data-testid="download-statement-btn">
              <FileDown className="w-3.5 h-3.5" />
              {downloading ? "Preparing…" : "Download PDF Statement"}
            </Button>
          </div>
          <Field k="Full Name" v={user.full_name} />
          <Field k="Email" v={user.email} mono />
          <Field k="Mobile" v={user.phone || "—"} mono />
          <Field k="Country" v={user.country || "—"} />
          <Field k="Balance" v={fmtUSD(user.balance)} mono highlight />
          <Field k="Leverage" v={`1:${user.leverage}`} mono />
        </div>

        <form onSubmit={changePw} className="bg-card p-8 space-y-5">
          <div>
            <p className="label-eyebrow mb-2">Security</p>
            <h3 className="text-lg font-semibold">Change Password</h3>
          </div>
          <div>
            <Label className="label-eyebrow">Current Password</Label>
            <Input type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className="mt-2 h-11 bg-background border-subtle" required data-testid="pw-old" />
          </div>
          <div>
            <Label className="label-eyebrow">New Password</Label>
            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-2 h-11 bg-background border-subtle" required data-testid="pw-new" />
          </div>
          <div>
            <Label className="label-eyebrow">Confirm New Password</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-2 h-11 bg-background border-subtle" required data-testid="pw-confirm" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11" data-testid="submit-change-password">
            {busy ? "Updating…" : "Update Password"}
          </Button>
        </form>
      </div>

      <div className="bg-card p-8 max-w-5xl" data-testid="profile-bank">
        <div className="flex items-center gap-2 mb-2">
          <Landmark className="w-4 h-4 text-primary" />
          <p className="label-eyebrow">Payout / Bank Information</p>
        </div>
        <h3 className="text-lg font-semibold mb-4">Your registered bank details</h3>
        {hasBank ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
            {bank.bank_name && <Field k="Bank Name" v={bank.bank_name} />}
            {bank.account_name && <Field k="Account Holder" v={bank.account_name} />}
            {bank.account_number && <Field k="Account Number" v={bank.account_number} mono />}
            {bank.ifsc && <Field k="IFSC / Routing" v={bank.ifsc} mono />}
            {bank.swift && <Field k="SWIFT / BIC" v={bank.swift} mono />}
            {bank.notes && (
              <div className="md:col-span-2 pt-2">
                <div className="text-sm text-muted-foreground mb-1">Additional notes</div>
                <div className="text-sm font-mono-num whitespace-pre-wrap">{bank.notes}</div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Your bank details are not yet configured. Please contact your account manager to have them added.</p>
        )}
      </div>
    </div>
  );
}

function Field({ k, v, mono = false, highlight = false }) {
  return (
    <div className="flex justify-between py-2 border-b border-subtle last:border-0">
      <span className="text-sm text-muted-foreground">{k}</span>
      <span className={`text-sm ${mono ? "font-mono-num" : ""} ${highlight ? "font-semibold text-profit" : "text-foreground"}`}>{v}</span>
    </div>
  );
}
